import {
  CircularProgress,
  createStyles,
  IconButton,
  InputAdornment,
  TextField as TextInput,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import { withStyles } from '@material-ui/styles';
import FilterChips from 'components/list/FilterChips';
import React from 'react';

const styles = (theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      marginRight: theme.spacing(2),
    },
    textInput: {
      borderRadius: theme.spacing(3.5),
      backgroundColor: 'white',
      boxShadow: theme.shadows[1],

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
        borderRadius: theme.spacing(3.5),
      },

      '& .MuiOutlinedInput-input': {
        padding: '12.5px 14px',
      },
    },
    startAdornment: {
      marginLeft: theme.spacing(-1.5),
      marginRight: theme.spacing(2),
    },
    endAdornment: {
      marginRight: theme.spacing(-1),
    },
    filterButton: {
      color: 'white',
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
      borderRadius: 0,
      borderBottomLeftRadius: theme.spacing(3.5),
      borderTopLeftRadius: theme.spacing(3.5),
      backgroundColor: '#C6C9E0',
      padding: theme.spacing(1.25, 2),
    },
  });

const SearchInput = ({
  input,
  label,
  classes,
  filterButton,
  onSearchChange,
  onSearchClear,
  autoSelectOnFocus = false,
  clearable = false,
  loading,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const shouldAutoSelectOnFocus = React.useRef(true);
  const hasValue = input.value !== undefined && input.value !== null && input.value !== '';
  const showClearButton = clearable && hasValue;
  const showLoading = loading && hasValue;

  const selectSearchText = () => {
    setTimeout(() => {
      if (inputRef.current === document.activeElement) {
        inputRef?.current?.select();
      }
    });
  };

  const handleChange = (event) => {
    shouldAutoSelectOnFocus.current = false;
    input.onChange(event);
    if (onSearchChange) onSearchChange(event.target.value);
  };

  const handleFocus = (event) => {
    input.onFocus(event);

    if (!autoSelectOnFocus || !event.target.value || !shouldAutoSelectOnFocus.current) return;

    selectSearchText();
  };

  const handleBlur = (event) => {
    input.onBlur(event);
    shouldAutoSelectOnFocus.current = true;
  };

  const handleSearchTextMouseUp = (event) => {
    if (!autoSelectOnFocus || !event.currentTarget.value || !shouldAutoSelectOnFocus.current) {
      return;
    }

    event.preventDefault();
    selectSearchText();
    shouldAutoSelectOnFocus.current = false;
  };

  const handleSearchTextClick = (event) => {
    if (!autoSelectOnFocus || !event.currentTarget.value || !shouldAutoSelectOnFocus.current) {
      return;
    }

    selectSearchText();
    shouldAutoSelectOnFocus.current = false;
  };

  const handleClearMouseDown = (event) => {
    event.preventDefault();
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    input.onChange('');
    if (onSearchClear) onSearchClear();
  };

  return (
    <div className={classes.root}>
      <TextInput
        fullWidth
        className={classes.textInput}
        {...input}
        inputRef={inputRef}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        variant="outlined"
        placeholder={label}
        inputProps={{
          onClick: handleSearchTextClick,
          onMouseUp: handleSearchTextMouseUp,
          onTouchEnd: handleSearchTextClick,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" className={classes.startAdornment}>
              {React.cloneElement(filterButton, {
                ...filterButton.props,
                className: classes.filterButton,
              })}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="start" className={classes.endAdornment}>
              {showClearButton && (
                <IconButton
                  type="button"
                  aria-label="Clear search"
                  onMouseDown={handleClearMouseDown}
                  onClick={handleClear}>
                  <CloseIcon />
                </IconButton>
              )}
              {showLoading ? (
                <CircularProgress size={25} style={{ marginRight: 10 }} />
              ) : (
                <IconButton type="submit">
                  <SearchRoundedIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      <FilterChips {...props} />
    </div>
  );
};

export default withStyles(styles)(SearchInput);
